/**
 * Custom auth hook wrapping Amplify's useAuthenticator
 * 
 * Provides:
 * - Current user information including name from Cognito attributes
 * - Admin status checking
 * - Sign out functionality
 * - Access token retrieval for API calls
 */

import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { useState, useEffect, useCallback } from 'react';

export interface AuthUser {
  username: string;
  userId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  signInDetails?: {
    loginId?: string;
  };
}

export interface UseAuthResult {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isLoadingAttributes: boolean;
  isAdmin: boolean;
  signOut: () => void;
  getAccessToken: () => Promise<string | undefined>;
  refreshAdminStatus: () => Promise<void>;
}

export function useAuth(): UseAuthResult {
  const { user, signOut, authStatus } = useAuthenticator((context) => [
    context.user,
    context.authStatus,
  ]);
  
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);
  const [isLoadingAttributes, setIsLoadingAttributes] = useState(true);
  const [userAttributes, setUserAttributes] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
  }>({});

  /**
   * Fetch user attributes from Cognito (given_name, family_name, email)
   */
  const fetchAttributes = useCallback(async () => {
    if (!user) {
      setUserAttributes({});
      setIsLoadingAttributes(false);
      return;
    }

    setIsLoadingAttributes(true);
    try {
      const attributes = await fetchUserAttributes();
      setUserAttributes({
        firstName: attributes.given_name,
        lastName: attributes.family_name,
        email: attributes.email,
      });
    } catch (error) {
      console.error('Error fetching user attributes:', error);
      setUserAttributes({});
    } finally {
      setIsLoadingAttributes(false);
    }
  }, [user]);

  /**
   * Check if user is in admin group
   */
  const checkAdminStatus = useCallback(async () => {
    if (!user) {
      setIsAdmin(false);
      setIsCheckingAdmin(false);
      return;
    }

    try {
      const session = await fetchAuthSession();
      const groups = session.tokens?.accessToken?.payload['cognito:groups'];
      
      let isAdminUser = false;
      if (typeof groups === 'string') {
        isAdminUser = groups.includes('admin');
      } else if (Array.isArray(groups)) {
        isAdminUser = groups.includes('admin');
      }
      
      setIsAdmin(isAdminUser);
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    } finally {
      setIsCheckingAdmin(false);
    }
  }, [user]);

  // Fetch user attributes and check admin status when user changes
  useEffect(() => {
    if (authStatus === 'authenticated') {
      checkAdminStatus();
      fetchAttributes();
    } else {
      setIsAdmin(false);
      setUserAttributes({});
      setIsCheckingAdmin(authStatus === 'configuring');
      setIsLoadingAttributes(authStatus === 'configuring');
    }
  }, [authStatus, checkAdminStatus, fetchAttributes]);

  /**
   * Get current ID token for API calls (Cognito Authorizer expects ID token)
   */
  const getAccessToken = useCallback(async (): Promise<string | undefined> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString();
    } catch (error) {
      console.error('Error getting ID token:', error);
      return undefined;
    }
  }, []);

  return {
    user: user ? {
      username: user.username,
      userId: user.userId,
      firstName: userAttributes.firstName,
      lastName: userAttributes.lastName,
      email: userAttributes.email,
      signInDetails: user.signInDetails,
    } : null,
    isAuthenticated: authStatus === 'authenticated',
    isLoading: authStatus === 'configuring' || isCheckingAdmin,
    isLoadingAttributes,
    isAdmin,
    signOut,
    getAccessToken,
    refreshAdminStatus: checkAdminStatus,
  };
}

export default useAuth;
